'use client';

import { Button } from '@/components/Button';
import { HeaderMetaLogin } from '@/components/HeaderMeta';
import useLanguage from '@/utils/hooks/useLanguage';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';

import { signIn } from 'next-auth/react';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

export function LoginUI({ loginDict, headerDict }: Readonly<{ loginDict: any; headerDict: any }>) {
	const { currentLocale } = useLanguage();
	const searchParams = useSearchParams();

	const callbackUrl = useMemo(() => {
		const redirectUrl = searchParams.get('redirect');

		return `/${currentLocale}${redirectUrl?.length ? redirectUrl : ''}`;
	}, [currentLocale, searchParams]);

	return (
		<section className="relative min-h-screen">
			<HeaderMetaLogin dict={headerDict} />
			<Image src="/background_road.webp" fill alt="bg" priority className="z-0 object-cover object-center" />
			<div className="flex flex-col w-screen min-h-[calc(100vh-3.5rem)] py-14 md:py-10 md:justify-center items-center overflow-auto">
				<div className="flex-1 z-10 relative md:block min-w-[15rem] md:min-w-[30rem] max-h-[3rem] max-w-lg h-16 self-start ml-5 md:ml-14 lg:ml-40 mb-8 lg:mb-0">
					<Image src="/Oerlikon_logo_white.svg" fill alt="Oerlikon logo" priority />
				</div>
				<div className="z-10 flex flex-1 w-11/12 md:w-3/4 max-w-6xl flex-col md:flex-row m-auto">
					<div className="flex flex-col lg:flex-row lg:self-center">
						<div className="flex-1 bg-black/30 backdrop-blur-lg border border-white/20 shadow-lg py-10 md:pt-12 md:pb-20 px-10 md:px-12">
							<h3 className="text-white mb-6 text-2xl md:text-[2rem] text-center md:text-left font-semibold drop-shadow-lg">{loginDict.welcome}</h3>
							<div className="text-white mt-5 bg-black/20 backdrop-blur-sm border border-white/10 p-4 rounded-lg shadow-md">
								<ExclamationTriangleIcon className="w-8 h-8 mr-4 mt-1 float-left text-yellow-400" />

								<h6 className="font-normal leading-relaxed">
									{loginDict.disclaimer1}{' '}
									<Link
										href="https://www.valtech.com/industries/manufacturing-distribution/manufacturing-and-digital-platforms/"
										className="underline hover:text-black-20"
										target="_blank"
										rel="noreferrer"
									>
										{loginDict.disclaimer2}
									</Link>{' '}
									{loginDict.disclaimer3}
								</h6>
							</div>
							<div className="mt-6 space-y-4 sm:space-y-0 sm:space-x-6 flex flex-col sm:flex-row items-start md:items-center justify-center md:justify-start">
								<Button
									type="button"
									variant="primary"
									handleButtonClick={() => signIn('azure-ad', { callbackUrl })}
									data-testid="login-submit"
								>
									{loginDict.loginButton}
								</Button>
								<h6 className="font-normal text-white">
									{loginDict.demo1}{' '}
									{
										<Link href="mailto:david.vander.hoop@valtech.com" className="underline hover:text-black-20">
											{loginDict.demo2}
										</Link>
									}
								</h6>
							</div>
						</div>
						<div className="z-10 flex-1 bg-neutral-100 px-10 md:px-12 py-10 md:pt-12 md:pb-20">
							<h4 className="font-medium mb-6">{loginDict.register1}</h4>
							<h6 className="font-medium mb-6">{loginDict.register2}</h6>
							<ul>
								<li>{loginDict.registerOptions1}</li>
								<li>{loginDict.registerOptions2}</li>
								<li>{loginDict.registerOptions3}</li>
								<li>{loginDict.registerOptions4}</li>
								<li>{loginDict.registerOptions5}</li>
							</ul>
							<Button
								type="button"
								variant="secondary"
								size="xs"
								handleButtonClick={() => {
									window.location.href = 'mailto:david.vander.hoop@valtech.com';
								}}
								className="mt-6"
							>
								{loginDict.requestAccount}
							</Button>
						</div>
					</div>
				</div>
				<div className="flex flex-1" />
			</div>
		</section>
	);
}

export default LoginUI;